import {
  DEFAULT_LOG_SEARCH_OPTIONS,
  LogSearchFilter,
  LogSearchOptions,
  normalizeLogSearchOptions,
} from './redux';
import { StaffMaps, StaffLike } from './logActor';
import { CustomerMaps, CustomerLike } from './logCustomer';
import { dedupeLogsById } from './logUtils';
import {
  stripLogSearchMarks,
  unwrapLogWithLegacy,
  wrapLogRaw,
} from './logFormat';

export type LogSearchable = {
  id?: string | number,
  message?: string | null,
  created_by?: string | null,
  type?: string | null,
  department?: string | null,
  inventory_type?: string | null,
  /** Stay-scoped activityLogs (Key card access, etc.). */
  booking_id?: string | null,
};

/** Sets of archived activity labels (shown with delete icon in ActivityLogs). */
export type LogSearchArchiveSets = {
  deletedTypes?: Set<string>,
  deletedGroups?: Set<string>,
  deletedDepartments?: Set<string>,
};

/** Strip log markup delimiters so e.g. `Heineken` matches `&Heineken&`. */
export const stripLogSearchSpecialChars = (value: string): string => (
  stripLogSearchMarks(value)
);

export const isDefaultLogSearchOptions = (
  options: LogSearchOptions,
): boolean => {
  const o = normalizeLogSearchOptions(options);
  return o.exactMatch === DEFAULT_LOG_SEARCH_OPTIONS.exactMatch
    && o.negate === DEFAULT_LOG_SEARCH_OPTIONS.negate
    && o.hideArchives === DEFAULT_LOG_SEARCH_OPTIONS.hideArchives
    && o.fromFullLogs === DEFAULT_LOG_SEARCH_OPTIONS.fromFullLogs
    && o.matchMode === DEFAULT_LOG_SEARCH_OPTIONS.matchMode;
};

/** Case-insensitive compare: equality when `exact`, otherwise substring. */
const textMatches = (haystack: string, needle: string, exact: boolean): boolean => {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (!n) return true;
  return exact ? h === n : h.includes(n);
};

const compactTextMatches = (haystack: string, needle: string, exact: boolean): boolean => {
  const h = haystack.replace(/\s+/g, '').toLowerCase();
  const n = needle.replace(/\s+/g, '').toLowerCase();
  if (!n) return true;
  return exact ? h === n : h.includes(n);
};

/** Same sanitization as check-in/out &-&…&-& booking tokens. */
const sanitizeBookingId = (id: string): string => (
  String(id || '').replace(/(?:-|_|:)+/g, '')
);

/** Match search term against structured booking_id (raw or sanitized). */
const matchBookingId = (
  bookingId: string | null | undefined,
  term: string,
  exact: boolean,
): boolean => {
  if (bookingId == null || String(bookingId) === '') return false;
  const bid = String(bookingId);
  const compact = sanitizeBookingId(bid);
  if (exact) return bid === term || compact === term;
  return textMatches(bid, term, false)
    || textMatches(compact, term, false)
    || compactTextMatches(bid, term, false);
};

/** Combine committed chips with the live draft into sequential search filters. */
export const collectLogSearchQueries = (
  logSearches: LogSearchFilter[],
  logSearch: string,
  logSearchOptions: LogSearchOptions,
): LogSearchFilter[] => ([
  ...logSearches,
  ...(logSearch.trim()
    ? [{ term: logSearch.trim(), options: normalizeLogSearchOptions(logSearchOptions) }]
    : []),
]);

export const hasActiveLogSearch = (
  logSearches: LogSearchFilter[],
  logSearch: string,
): boolean => logSearches.length > 0 || Boolean(logSearch.trim());

const setHasCI = (set: Set<string> | undefined, value: string | null | undefined) => {
  if (!set?.size || value == null || value === '') return false;
  const v = String(value).trim().toLowerCase();
  if (!v) return false;
  if (set.has(v)) return true;
  for (const entry of set) {
    if (entry.toLowerCase() === v) return true;
  }
  return false;
};

/** Whether a log belongs to an archived activity excluded by these options. */
export const logIsArchivedForSearch = (
  log: LogSearchable,
  options: LogSearchOptions,
  archives?: LogSearchArchiveSets | null,
): boolean => {
  if (!archives) return false;
  const o = normalizeLogSearchOptions(options);
  if (!o.hideArchives) return false;
  if (setHasCI(archives.deletedTypes, log.type)) {
    return true;
  }
  if (setHasCI(archives.deletedGroups, log.type)
    || setHasCI(archives.deletedGroups, log.inventory_type)) {
    return true;
  }
  if (setHasCI(archives.deletedDepartments, log.department)
    || setHasCI(archives.deletedDepartments, log.type)) {
    return true;
  }
  return false;
};

/** Channel-manager `&C&key&C&` tokens → guest-facing OTA name. */
const OTA_CHANNEL_LABELS: Record<string, string> = {
  BookingCom: 'Booking.com',
  Expedia: 'Expedia',
  Airbnb: 'Airbnb',
  Agoda: 'Agoda',
  HotelsCom: 'Hotels.com',
};

/** Values inside room wraps — not whole-message. */
const matchRoomName = (messageRaw: string, term: string, exact: boolean): boolean => {
  if (!term.trim()) return true;
  const rooms = unwrapLogWithLegacy('room', messageRaw);
  for (let i = 0; i < rooms.length; i += 1) {
    if (textMatches((rooms[i] || '').trim(), term, exact)) return true;
  }
  return false;
};

/** Department field + real department `!…!` tags only (never type-prefix bangs). */
const matchDepartment = (
  log: LogSearchable,
  term: string,
  departmentNames?: Set<string> | null,
  exact = false,
): boolean => {
  if (!term.trim()) return true;

  const deptField = (log.department || '').trim();
  if (deptField && textMatches(deptField, term, exact)) return true;

  const isKnownDepartment = (name: string) => {
    if (deptField && name.toLowerCase() === deptField.toLowerCase()) return true;
    return setHasCI(departmentNames || undefined, name);
  };

  const messageRaw = log.message || '';
  const depts = unwrapLogWithLegacy('dept', messageRaw);
  for (let i = 0; i < depts.length; i += 1) {
    const tag = (depts[i] || '').trim();
    if (tag && textMatches(tag, term, exact) && isKnownDepartment(tag)) {
      return true;
    }
  }

  // Legacy department activityLogs used type = department name.
  const logType = (log.type || '').trim();
  if (logType
    && textMatches(logType, term, exact)
    && isKnownDepartment(logType)) {
    return true;
  }
  return false;
};

/** Staff markers (`|…|`) — match resolved / legacy usernames only (never staff ids). */
const matchStaffUsername = (
  log: LogSearchable,
  term: string,
  staffMaps?: StaffMaps | null,
  exact = false,
): boolean => {
  const s = term.trim();
  if (!s) return true;
  const messageRaw = log.message || '';
  const nameMatches = (uname: string) => (
    textMatches(uname, s, exact) || compactTextMatches(uname, s, exact)
  );

  const tokens = unwrapLogWithLegacy('actor', messageRaw);
  for (let i = 0; i < tokens.length; i += 1) {
    const token = (tokens[i] || '').trim();
    if (!token) continue;
    const user = staffMaps
      ? (
        staffMaps.byId.get(token)
        || staffMaps.byUsername.get(token.replace(/\s+/g, '').toLowerCase())
        || staffMaps.byUsername.get(token.toLowerCase())
      )
      : undefined;
    if (user?.username) {
      if (nameMatches(user.username)) return true;
    } else if (!/^\d+$/.test(token) && nameMatches(token)) {
      return true;
    }
  }

  if (staffMaps && log.created_by != null && String(log.created_by) !== '') {
    const byId = staffMaps.byId.get(String(log.created_by));
    if (byId?.username && nameMatches(byId.username)) return true;
  }
  return false;
};

/** Customer markers (`&G&…&G&`) — match resolved / legacy names only (never raw ids). */
const matchCustomerName = (
  log: LogSearchable,
  term: string,
  customerMaps?: CustomerMaps | null,
  exact = false,
): boolean => {
  const s = term.trim();
  if (!s) return true;
  const messageRaw = log.message || '';
  const nameMatches = (cname: string) => (
    textMatches(cname, s, exact) || compactTextMatches(cname, s, exact)
  );

  const tokens = unwrapLogWithLegacy('customer', messageRaw);
  for (let i = 0; i < tokens.length; i += 1) {
    const token = (tokens[i] || '').trim();
    if (!token) continue;
    const customer = customerMaps
      ? (
        customerMaps.byId.get(token)
        || customerMaps.byName.get(token.replace(/\s+/g, '').toLowerCase())
        || customerMaps.byName.get(token.toLowerCase())
      )
      : undefined;
    if (customer?.name) {
      if (nameMatches(customer.name)) return true;
    } else if (!/^(c_)?[a-z0-9]+$/i.test(token) || token.includes(' ')) {
      if (nameMatches(token)) return true;
    } else if (nameMatches(token)) {
      return true;
    }
  }
  return false;
};

/** OTA markers (`&C&…&C&`) — match channel key or display label only. */
const matchOtaName = (messageRaw: string, term: string, exact: boolean): boolean => {
  if (!term.trim()) return true;
  const nameMatches = (value: string) => (
    textMatches(value, term, exact) || compactTextMatches(value, term, exact)
  );
  const tokens = unwrapLogWithLegacy('ota', messageRaw);
  for (let i = 0; i < tokens.length; i += 1) {
    const token = (tokens[i] || '').trim();
    if (token) {
      if (nameMatches(token)) return true;
      const label = OTA_CHANNEL_LABELS[token]
        || Object.entries(OTA_CHANNEL_LABELS).find(
          ([key]) => key.toLowerCase() === token.toLowerCase(),
        )?.[1];
      if (label && nameMatches(label)) return true;
    }
  }
  return false;
};

/** Match a search term against a log (message / actor / staff / customer / typed modes). */
export const logMatchesSearchTerm = (
  log: LogSearchable,
  term: string,
  staffMaps?: StaffMaps | null,
  options?: LogSearchOptions | null,
  departmentNames?: Set<string> | null,
  customerMaps?: CustomerMaps | null,
): boolean => {
  const s = term || '';
  if (!s) return true;
  const o = normalizeLogSearchOptions(options);
  const messageRaw = log.message || '';
  const exact = o.exactMatch;

  if (o.matchMode === 'room') {
    return matchRoomName(messageRaw, s, exact);
  }
  if (o.matchMode === 'staff') {
    return matchStaffUsername(log, s, staffMaps, exact);
  }
  if (o.matchMode === 'customer') {
    return matchCustomerName(log, s, customerMaps, exact);
  }
  if (o.matchMode === 'department') {
    return matchDepartment(log, s, departmentNames, exact);
  }
  if (o.matchMode === 'ota') {
    return matchOtaName(messageRaw, s, exact);
  }

  // Prefer structured booking_id (Key card / stay-scoped activityLogs)
  if (matchBookingId(log.booking_id, s, exact)) return true;

  // Legacy / message-embedded &-&bookingId&-& tokens (check-in style)
  const compactTerm = sanitizeBookingId(s);
  if (compactTerm && (
    messageRaw.includes(wrapLogRaw('booking', s))
    || messageRaw.includes(wrapLogRaw('booking', compactTerm))
    || messageRaw.includes(`&-&${s}&-&`)
    || messageRaw.includes(`&-&${compactTerm}&-&`)
  )) {
    return true;
  }

  const message = stripLogSearchSpecialChars(messageRaw).toLowerCase();
  if (textMatches(message, s, exact)) return true;
  if (String(log.created_by || '') === s) return true;

  // Resolve staff tags by username for general search
  if (staffMaps) {
    const matchedStaff = new Set<StaffLike>();
    staffMaps.byId.forEach((user) => {
      const uname = user.username || '';
      if (textMatches(uname, s, exact) || compactTextMatches(uname, s, exact)) {
        matchedStaff.add(user);
      }
    });
    staffMaps.byUsername.forEach((user) => {
      const uname = user.username || '';
      if (textMatches(uname, s, exact) || compactTextMatches(uname, s, exact)) {
        matchedStaff.add(user);
      }
    });
    for (const user of matchedStaff) {
      if (user.id != null && String(user.id) !== '') {
        if (messageRaw.includes(wrapLogRaw('actor', user.id))
          || messageRaw.includes(`|${user.id}|`)) return true;
        if (String(log.created_by || '') === String(user.id)) return true;
      }
      if (user.username) {
        const uname = user.username.toLowerCase();
        if (messageRaw.toLowerCase().includes(wrapLogRaw('actor', uname).toLowerCase())
          || messageRaw.toLowerCase().includes(`|${uname}|`)) return true;
        if (textMatches(message, uname, exact)) return true;
      }
    }

    const byId = staffMaps.byId.get(s);
    if (byId?.username) {
      const uname = byId.username.toLowerCase();
      if (messageRaw.toLowerCase().includes(wrapLogRaw('actor', uname).toLowerCase())
        || messageRaw.toLowerCase().includes(`|${uname}|`)) return true;
      if (textMatches(message, uname, exact)) return true;
    }
  }

  // Resolve customer tags by name for general search
  if (customerMaps) {
    const matchedCustomers = new Set<CustomerLike>();
    customerMaps.byId.forEach((customer) => {
      const cname = customer.name || '';
      if (textMatches(cname, s, exact) || compactTextMatches(cname, s, exact)) {
        matchedCustomers.add(customer);
      }
    });
    customerMaps.byName.forEach((customer) => {
      const cname = customer.name || '';
      if (textMatches(cname, s, exact) || compactTextMatches(cname, s, exact)) {
        matchedCustomers.add(customer);
      }
    });
    for (const customer of matchedCustomers) {
      if (customer.id != null && String(customer.id) !== '') {
        if (messageRaw.includes(wrapLogRaw('customer', customer.id))
          || messageRaw.includes(`&G&${customer.id}&G&`)) return true;
      }
      if (customer.name) {
        const cname = customer.name.toLowerCase();
        if (messageRaw.toLowerCase().includes(wrapLogRaw('customer', cname).toLowerCase())
          || messageRaw.toLowerCase().includes(`&G&${cname}&G&`)) return true;
        if (textMatches(message, cname, exact)) return true;
      }
    }
  }

  return false;
};

/**
 * Apply sequential search filters.
 * Default: AND — each chip narrows the previous remainder.
 * `fromFullLogs` (2nd+ only): search the result set from before the previous
 * search (not the original full log list) and union those hits into current.
 */
export const filterLogsBySearch = <T extends LogSearchable>(
  logs: T[],
  queries: LogSearchFilter[],
  staffMaps?: StaffMaps | null,
  archives?: LogSearchArchiveSets | null,
  departmentNames?: Set<string> | null,
  customerMaps?: CustomerMaps | null,
): T[] => {
  if (!queries.length) return logs;
  let next = logs;
  // Results as they were before the most recent search chip was applied.
  let beforePrevious = logs;
  queries.forEach((query, index) => {
    const rawOptions = query.options
      ?? { negate: Boolean((query as { negate?: boolean }).negate) };
    const o = normalizeLogSearchOptions(rawOptions);
    const t = (query.term || '').trim();

    const pass = (l: T) => {
      if (logIsArchivedForSearch(l, o, archives)) return false;
      if (!t) return true;
      const matches = logMatchesSearchTerm(
        l, t, staffMaps, o, departmentNames, customerMaps,
      );
      return o.negate ? !matches : matches;
    };

    // 2nd+ chip: match within results-before-previous-search, add to current.
    if (o.fromFullLogs && index > 0) {
      // `next` is the previous search results; `beforePrevious` is the pool before that.
      const previousResults = next;
      const added = beforePrevious.filter(pass);
      next = dedupeLogsById([...next, ...added]);
      // Next ADD should search from these previous results, not the original full set.
      beforePrevious = previousResults;
      return;
    }

    // AND narrow: parent of the new results becomes the next “before previous”.
    beforePrevious = next;
    next = next.filter(pass);
  });
  return next;
};
